import { type FC, lazy, Suspense, useEffect, useRef, useState } from 'react';

import { createSelector } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';

import { _isEmpty, _isNotEmpty, _notNil } from '@/littledash';

import Banner from '@/components/UI/Banner';
import Checkbox from '@/components/UI/FormElements/Checkbox';
import Loading from '@/components/Loading';
import { NoDataMessage } from '@/components/NoDataCard';
import { errorToast } from '@/helpers.tsx';
import type { AnimalApiId } from '@/model/Animal.model.ts';
import InVivoError from '@/model/InVivoError.ts';
import { ApiService } from '@/support/ApiService';
import { featuresSelector } from '@/support/Selectors';
import { ExceptionHandler } from '@/utils/ExceptionHandler.ts';

import { applyDosageChanges } from './Summary.utils';
import type { ExcludedDosages, SummaryHotRefCurrent, SummaryStepProps } from './Summary.models';

/**
 * Lazy load table versions to isolate HOT v12/v16 module registration and editor/validator side effects.
 * Only the selected version is loaded based on `hot_upgrade`.
 */
const SummarySpreadSheetV12 = lazy(() => import('./SpreadSheetV12'));
const SummarySpreadSheetV16 = lazy(() => import('./SpreadSheetV16'));

const hotUpgradeSelector = createSelector([featuresSelector], (features) => ({
  hotUpgrade: features?.hot_upgrade ?? false,
}));

export const Summary: FC<SummaryStepProps> = ({ state, dispatch }) => {
  const { hotUpgrade } = useSelector(hotUpgradeSelector);
  const [loading, setLoading] = useState(true);
  const [excludedDoses, setExcludedDoses] = useState<ExcludedDosages>({ weight_filtered: 0, deceased_filtered: 0 });
  const [emptyInductedDateCount, setEmptyInductedDateCount] = useState<number>(0);
  const [addDiseaseInductionDate, setDiseaseInduction] = useState<boolean>(true);

  const {
    study,
    animals: { metadata },
  } = state;
  const classification = state.treatmentClassification ?? 'treatment';

  const tableRef = useRef<SummaryHotRefCurrent>(null);

  useEffect(() => {
    const hotInstance = tableRef.current?.hotInstance;
    if ((state.dosages?.length ?? 0) > 0 && _notNil(hotInstance)) {
      new Promise<boolean>((resolve) => hotInstance.validateCells(resolve)).then((valid) => {
        if (state.finalStepReady !== valid) {
          dispatch({ type: 'finalStepReady', data: valid });
        }
      });
    }
  }, [dispatch, state.dosages, state.finalStepReady]);

  useEffect(() => {
    const animalIds: Array<AnimalApiId> = state.selectedAnimals.reduce((acc: Array<AnimalApiId>, animal) => {
      if (_notNil(animal?.api_id)) {
        acc.push(animal.api_id);
      }
      return acc;
    }, []);
    const treatmentIds = state.selectedTreatments.map(({ api_id }) => api_id);

    ApiService.call({
      endpoint: 'POST /api/v1/studies/{studyId}/dosages/calculate',
      path: { studyId: study!.api_id },
      body: { animals: animalIds, treatments: treatmentIds },
    })
      .then((result) => {
        const dosages = (result.body?.dosages ?? []).map((dosage) => ({
          ...dosage,
          dosed_at: new Date().toISOString(),
        }));

        setExcludedDoses({
          deceased_filtered: result.body?.deceased_filtered_out_count ?? 0,
          weight_filtered: result.body?.weight_filtered_out_count ?? 0,
        });
        setEmptyInductedDateCount(result.body?.disease_inducted_empty_count ?? 0);

        dispatch({
          type: 'updateArray',
          id: 'dosages',
          data: dosages,
        });
        setLoading(false);
      })
      .catch((error) => {
        errorToast('Failed to calculate dosages');
        ExceptionHandler.captureException(
          new InVivoError('Could not calculate dosages', {
            cause: error,
            slug: 'calculate-bulk-dosages',
          })
        );
      });
    // Run once when entering Summary step to initialize dosing rows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="pt4">
        <Loading />
      </div>
    );
  }

  return (
    <div className="pt4" data-testid="calculate-dosages-container">
      <div className="flex flex-row">
        {!(excludedDoses.weight_filtered === 0 && excludedDoses.deceased_filtered === 0) && (
          <div className="flex flex-column">
            <Banner info className="mw6 mr4 mb4" testId={'banner-dosage-exclusion'}>
              <h3 className="f6 lh-copy fw5 pb3">One or more dosages have been excluded for the following reasons:</h3>
              <ul>
                {excludedDoses.weight_filtered !== 0 && (
                  <li className="o-list ml3 f6 lh-copy near-black">
                    {excludedDoses.weight_filtered} dosage(s) excluded because animals did not have recorded
                    measurements
                  </li>
                )}
                {excludedDoses.deceased_filtered !== 0 && (
                  <li className="o-list ml3 f6 lh-copy near-black">
                    {excludedDoses.deceased_filtered} dosage(s) excluded because animals were marked as deceased
                  </li>
                )}
              </ul>
            </Banner>
          </div>
        )}
        {emptyInductedDateCount !== 0 && classification === 'disease_induction' && (
          <div className="flex flex-column">
            <Banner info className="mw6 mr4 mb4" testId={'banner-dosage-induction'}>
              <h3>
                <span className="f6 lh-copy fw5 pb3">
                  {emptyInductedDateCount} animals do not have a disease induction date.
                </span>
                <div className="f6 pt2 lh-copy near-black">
                  This will be added automatically unless the checkbox below is disabled.
                </div>
              </h3>
            </Banner>
          </div>
        )}
      </div>

      {_isEmpty(state.dosages) && <NoDataMessage title="No doses have been recorded" />}

      <div className="ow-spreadsheet-styles">
        {_isNotEmpty(state.dosages) && (
          <Suspense
            fallback={
              <div className="pt4">
                <Loading />
              </div>
            }
          >
            {hotUpgrade ? (
              <SummarySpreadSheetV16
                dosages={state.dosages}
                metadata={metadata}
                innerRef={tableRef}
                onAfterChange={(changes) => applyDosageChanges(changes, state, dispatch)}
              />
            ) : (
              <SummarySpreadSheetV12
                dosages={state.dosages}
                metadata={metadata}
                innerRef={tableRef}
                onAfterChange={(changes) => applyDosageChanges(changes, state, dispatch)}
              />
            )}
          </Suspense>
        )}
      </div>

      {emptyInductedDateCount !== 0 && classification === 'disease_induction' && (
        <Checkbox
          label="Set Disease Induction Date"
          sublabel="This date will match the dosing date above for animals with this field undefined."
          id="disease-induction"
          name="disease-induction"
          data-testid="disease-induction-checkbox"
          checked={addDiseaseInductionDate}
          onChange={() => {
            dispatch({
              type: 'update',
              id: 'inductDisease',
              data: { induct: !addDiseaseInductionDate },
            });
            setDiseaseInduction(!addDiseaseInductionDate);
          }}
          className={`mv2`}
        />
      )}
    </div>
  );
};
